import React, { useState } from "react";
import Chart from "react-apexcharts";
import { formatDateForPeriod, getFormattedData } from "../dateUtils";
import { transformChartData } from "../charthelper";

const LineChart = ({ chartUtils, colors }) => {
    const [applyPeakFilter, setApplyPeakFilter] = useState(false);
    const [thresholdPercent, setThresholdPercent] = useState(300);
  
  const combinedData = chartUtils?.series?.flatMap((seriesItem) =>
    chartUtils?.category?.map((date, index) => ({
      date,
      value: seriesItem?.data?.[index] || 0,
      name: seriesItem.name,
    }))
  );

  const formattedData = getFormattedData(
    combinedData || [],
    chartUtils?.chart_period || "daily"
  );

  const getFontSize = () => {
    if (formattedData.categories?.length <= 20) return "12px";
    if (formattedData.categories?.length <= 50) return "10px";
    return "8px";
  };
  const options = {
    chart: {
      type: "line",
      zoom: { enabled: true, type: "x" },
      events: {
        mounted: function (chartContext, config) {
          setTimeout(() => {
            const chart = chartContext.el;
            // First, try to find all text elements in the chart
            const allTexts = chart.querySelectorAll('text');

            allTexts.forEach(textElement => {
              const text = textElement.textContent.trim();

              const isNumeric = /^\d+(\.\d+)?$/.test(text);
              const x = parseFloat(textElement.getAttribute('x')) || 0;
              const y = parseFloat(textElement.getAttribute('y')) || 0;

              // Additional checks to ensure it's a data label
              const parent = textElement.parentElement;
              const isDataLabel = parent && (
                parent.classList.contains('apexcharts-datalabel') ||
                parent.classList.contains('apexcharts-datalabels') ||
                parent.parentElement?.classList.contains('apexcharts-datalabels')
              );

              if (isNumeric && isDataLabel && x > 0 && y > 0) {
                // Apply rotation
                textElement.setAttribute('transform', `rotate(-45 ${x} ${y})`);
              }
            });
          }, 1000);
        },
        updated: function (chartContext, config) {
          setTimeout(() => {
            const chart = chartContext.el;
            const allTexts = chart.querySelectorAll('text');

            allTexts.forEach(textElement => {
              const text = textElement.textContent.trim();
              const isNumeric = /^\d+(\.\d+)?$/.test(text);
              const x = parseFloat(textElement.getAttribute('x')) || 0;
              const y = parseFloat(textElement.getAttribute('y')) || 0;

              const parent = textElement.parentElement;
              const isDataLabel = parent && (
                parent.classList.contains('apexcharts-datalabel') ||
                parent.classList.contains('apexcharts-datalabels') ||
                parent.parentElement?.classList.contains('apexcharts-datalabels')
              );

              if (isNumeric && isDataLabel && x > 0 && y > 0) {
                textElement.setAttribute('transform', `rotate(-45 ${x} ${y})`);
              }
            });
          }, 1000);
        },
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          reset: true
        }
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: { enabled: true, delay: 150 },
        dynamicAnimation: { enabled: true, speed: 500 }
      }
    },
    stroke: {
      curve: "smooth",
      width: 3,
      dashArray: [0, 5]
    },
    markers: {
      size: 5,
      colors: "#fff",
      strokeColors: colors || ["#008FFB"],
      strokeWidth: 3,
      hover: { size: 7 }
    },
    grid: {
      show: true,
      borderColor: "#E8E8E8",
      strokeDashArray: 0,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    xaxis: {
      type: "datetime",
      labels: {
        rotate: -45,
        datetimeUTC: false,
        text: chartUtils?.axis?.x || "Date",
      },
    },

    yaxis: {
      title: { text: chartUtils?.axis?.y || "Value" },
      labels: {
        style: { fontSize: "12px", colors: "#6E759F" },
        formatter: (value) => value.toFixed(2)
      }
    },
    tooltip: {
      enabled: true,
      x: {
        formatter: (value, { dataPointIndex }) => {
          const originalDate = formattedData.categories[dataPointIndex];
          const dateObj = new Date(originalDate);
          return dateObj.toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
          });
        }
      },
      y: {
        formatter: (value) => `${value.toFixed(2)} units`
      }
    },
    colors: colors || ["#008FFB", "#00E396", "#FEB019", "#FF4560"],
    dataLabels: {
      enabled: formattedData.categories?.length <= 100,
      offsetY: -10,
      background: {
        enabled: false, // Removed background
      },
      style: {
        colors: colors || ["#008FFB", "#00E396", "#FEB019", "#FF4560"], // Match line colors
        fontSize: getFontSize(),
        fontWeight: "bold"
      },
      formatter: (value) => value.toFixed(2)
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "center",
      fontSize: "12px",
      fontWeight: 600,
      labels: { colors: "#333" },
      markers: { width: 12, height: 12, radius: 12 },
      itemMargin: { horizontal: 10, vertical: 5 }
    },
  };

  const series = formattedData.seriesData || [];
  // Peak filtering function

   const filterPeakValuesWithCategories = (series, categories, percentage) => {
    const flatValues = series.flatMap((s) => s.data).filter((v) => typeof v === "number");

    if (flatValues.length === 0) return { series, categories };

    const avg = flatValues.reduce((sum, val) => sum + val, 0) / flatValues.length;
    const threshold = avg * (1 + percentage / 100);

    console.log(`Average: ${avg.toFixed(2)}, Threshold (${percentage}% above avg): ${threshold.toFixed(2)}`);

    const indicesToRemove = new Set();

    series.forEach((s) => {
      s.data.forEach((value, index) => {
        if (typeof value === "number" && value > threshold) {
          console.log(`Filtering out peak value: ${value} at index ${index} for series: ${s.name}`);
          indicesToRemove.add(index);
        }
      });
    });

    console.log(`Removing ${indicesToRemove.size} data points with peak values`);

    const filteredSeries = series.map((s) => ({
      ...s,
      data: s.data.filter((_, index) => !indicesToRemove.has(index))
    }));

    const filteredCategories = categories.filter((_, index) => !indicesToRemove.has(index));

    return { series: filteredSeries, categories: filteredCategories };
  };

  const filteredResult = applyPeakFilter
    ? filterPeakValuesWithCategories(series, formattedData.categories, thresholdPercent)
    : { series, categories:formattedData.categories };

  const filteredSeries = filteredResult.series;
  const filteredCategories = filteredResult.categories;

  return (
    <>
      <div className="mb-4 flex gap-4 items-center">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={applyPeakFilter}
              onChange={(e) => setApplyPeakFilter(e.target.checked)}
            />
            Filter Peaks
          </label>

        </div>

   {chartUtils?.chart_type === "LINECHART" ? (
      <Chart
        options={options}
        series={transformChartData(filteredCategories, filteredSeries)}
        type="line"
        height={350}
        width="100%"
      />
    ) : null}
    </>
 
  );
};

export default LineChart;